import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';

import { PLATFORM_CONFIG } from '../../../../shared/constants';
import { PlatformIcon } from '../../../common/PlatformIcon';
import type { GroupData } from '../RecommendedGroupCardsContainer/types';

export const RecommendedGroupCardItem: React.FC<{
	group: GroupData;
	platform: 'facebook' | 'reddit';
	onClick?: () => void;
}> = ({ group, onClick }) => {
	const { localizeNumber } = useI18n();

	const { name, followers, platform, iconUrl, bgColor: groupBgColor } = group;

	const { bgColor } = PLATFORM_CONFIG[platform];

	const handleClick = () => onClick?.();

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			onClick?.();
		}
	};

	const isClickable = Boolean(onClick);

	return (
		<div
			className={cn('h-[174px] w-[124px] overflow-visible rounded-xl bg-white shadow-sm lg:w-36', {
				'cursor-pointer hover:ring-[3px] hover:ring-(--color-stroke-info-strong) active:ring-[3px] active:ring-(--color-stroke-info-strong)':
					isClickable,
			})}
			aria-label={`${platform} group: ${name}`}
			onClick={isClickable ? handleClick : undefined}
			onKeyDown={isClickable ? handleKeyDown : undefined}
			role={isClickable ? 'button' : undefined}
			tabIndex={isClickable ? 0 : undefined}
			data-qa={`recommended-group-card-${platform}`}
		>
			<figure
				className="bg-fill-weak relative flex h-24 items-center justify-center overflow-visible rounded-t-xl border-0 border-solid border-white bg-cover bg-center bg-no-repeat"
				style={{ backgroundColor: groupBgColor || bgColor }}
			>
				<PlatformIcon platform={platform} />
				{iconUrl ? (
					<div
						className="bg-fill-base aspect-square h-[67px] w-[67px] rounded-full bg-contain bg-center bg-no-repeat shadow"
						style={{
							backgroundImage: `url(${iconUrl})`,
							imageRendering: 'crisp-edges',
						}}
						role="img"
						aria-label={`${name} logo`}
					/>
				) : (
					<div
						data-testid="group-icon-fallback"
						className="h-14 w-14 rounded-full border-4 border-white shadow"
						aria-hidden="true"
					>
						<div className="to-fill-error h-full w-full rounded-full bg-linear-to-b from-transparent" />
					</div>
				)}
			</figure>

			<section className="bg flex w-32 flex-col items-start justify-center rounded-b-xl p-3 lg:w-36">
				<Text
					as="p"
					fontWeight="bold"
					size="small"
					className={cn('text-typography-strong w-full', {
						truncate: followers !== null,
						'line-clamp-2': followers === null,
					})}
				>
					{name}
				</Text>
				{followers !== null && (
					<Text as="p" variant="body" size="x-small" className="text-typography-weak w-full truncate">
						{localizeNumber(followers)} followers
					</Text>
				)}
			</section>
		</div>
	);
};
